import React from 'react';
import { useTranslation } from 'react-i18next';
import { rowActivation } from './rowActivation';
import { initials } from './rows';

/**
 * AccountCardRow (phone) + AccountGridRow (desktop table) — UXeng's Phase-2 Accounts spec
 * (fleet-reports/UXeng-phase2-per-screen.md §3): title `Account Name`, secondary `Industry · Location`,
 * meta-right `Total Deal Value`, `Primary Contact` as a muted line with avatar. ZERO pills.
 *
 * `totalDealValue` renders as a plain formatted NUMBER, not a currency string — Deal has no currency
 * field (a real gap, out of this screen's scope) and this codebase is multi-tenant/multi-locale, so
 * printing a "$" would assert a currency no field actually establishes.
 */
export interface AccountRow {
  id: string;
  name: string;
  industry?: string | null;
  location?: string | null;
  primaryContactName?: string | null;
  totalDealValue: number;
}

export interface AccountRowCallbacks {
  selected?: boolean;
  onOpenAccount: (account: AccountRow) => void;
}

function industryLocation(a: AccountRow, t: (k: string) => string): string {
  const parts = [a.industry, a.location].filter(Boolean);
  return parts.length ? parts.join(' · ') : t('accountsGrid.noIndustryOrLocation');
}

function formatValue(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

const PrimaryContactLine: React.FC<{ name?: string | null }> = ({ name }) => {
  const { t } = useTranslation('common');
  return (
    <div className="flex items-center gap-1.5 text-xs text-text-muted mt-1">
      {name && <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-canvas text-[9px] font-bold text-text-muted">{initials(name)}</span>}
      <span className="truncate">{name || t('accountsGrid.noPrimaryContact')}</span>
    </div>
  );
};

export const AccountCardRow: React.FC<{ account: AccountRow } & AccountRowCallbacks> = ({ account, selected, onOpenAccount }) => {
  const { t } = useTranslation('common');
  return (
    <div {...rowActivation(() => onOpenAccount(account))} onClick={() => onOpenAccount(account)}
      className={`p-3 rounded-xl bg-surface border ${selected ? 'ring-2 ring-primary/15 border-primary/30' : 'border-border-neutral'} cursor-pointer`}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-text-strong truncate">{account.name || '—'}</span>
        <span className="ms-auto text-[11px] text-text-muted shrink-0" dir="ltr">{formatValue(account.totalDealValue)}</span>
      </div>
      <div className="text-xs text-text-muted truncate">{industryLocation(account, t)}</div>
      <PrimaryContactLine name={account.primaryContactName} />
    </div>
  );
};

export const AccountGridRow: React.FC<{ account: AccountRow } & AccountRowCallbacks> = ({ account, selected, onOpenAccount }) => {
  const { t } = useTranslation('common');
  return (
    <tr tabIndex={0} onKeyDown={rowActivation(() => onOpenAccount(account)).onKeyDown} className={`border-b border-border-neutral hover:bg-canvas cursor-pointer ${selected ? 'bg-canvas' : ''}`} onClick={() => onOpenAccount(account)}>
      <td className="sticky z-10 bg-surface p-cell-y px-cell-x [inset-inline-start:0]">
        <span className="text-sm font-semibold text-text-strong truncate">{account.name || '—'}</span>
      </td>
      <td className="p-cell-y px-cell-x text-sm text-text-muted whitespace-nowrap truncate">{account.industry || '—'}</td>
      <td className="p-cell-y px-cell-x text-sm text-text-muted whitespace-nowrap truncate">{account.location || '—'}</td>
      <td className="p-cell-y px-cell-x text-sm text-text-muted whitespace-nowrap truncate">{account.primaryContactName || t('accountsGrid.noPrimaryContact')}</td>
      <td className="p-cell-y px-cell-x text-sm text-text-strong whitespace-nowrap" dir="ltr">{formatValue(account.totalDealValue)}</td>
    </tr>
  );
};
